import {
  MergeCandidateSession,
  MergePatientOption,
  TestSessionListEntry,
} from "@/types/testSessionTypes";

export const MIN_SESSIONS_TO_MERGE = 2;

export function toMergeCandidate(session: TestSessionListEntry): MergeCandidateSession {
  return {
    sessionId: session.sessionId,
    patientId: session.patientId ?? null,
    patientName: session.patientName ?? session.userName ?? session.username ?? null,
    testSuiteName: session.testSuiteName ?? null,
    startedAt: session.startedAt ?? null,
    resultCount: session.results?.length ?? 0,
  };
}

export function isSessionSelected(selection: MergeCandidateSession[], sessionId: string): boolean {
  return selection.some((session) => session.sessionId === sessionId);
}

export function toggleSelection(
  selection: MergeCandidateSession[],
  session: MergeCandidateSession,
): MergeCandidateSession[] {
  if (isSessionSelected(selection, session.sessionId)) {
    return selection.filter((selected) => selected.sessionId !== session.sessionId);
  }
  return [...selection, session];
}

export function collectMergePatientOptions(
  selection: MergeCandidateSession[],
): MergePatientOption[] {
  const optionsByPatientId = new Map<string, MergePatientOption>();
  for (const session of selection) {
    if (!session.patientId) continue;
    const existing = optionsByPatientId.get(session.patientId);
    if (existing) {
      existing.sessionCount += 1;
      existing.patientName = existing.patientName ?? session.patientName;
      continue;
    }
    optionsByPatientId.set(session.patientId, {
      patientId: session.patientId,
      patientName: session.patientName,
      sessionCount: 1,
    });
  }
  return [...optionsByPatientId.values()].sort(
    (a, b) =>
      b.sessionCount - a.sessionCount || (a.patientName ?? "").localeCompare(b.patientName ?? ""),
  );
}

export function canMergeSelection(selection: MergeCandidateSession[]): boolean {
  return (
    selection.length >= MIN_SESSIONS_TO_MERGE && collectMergePatientOptions(selection).length > 0
  );
}

export function earliestSessionStart(selection: MergeCandidateSession[]): string | null {
  const startTimes = selection
    .map((session) => session.startedAt)
    .filter((startedAt): startedAt is string => !!startedAt)
    .map((startedAt) => ({ startedAt, time: new Date(startedAt).getTime() }))
    .filter(({ time }) => Number.isFinite(time));
  if (startTimes.length === 0) return null;
  return startTimes.reduce((earliest, current) =>
    current.time < earliest.time ? current : earliest,
  ).startedAt;
}
